import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Settlement } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, InfoNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function SettlementDetail() {
  const { id = '' } = useParams();
  const { persona } = useAuth();
  const sq = useApiQuery<Settlement>(`/api/v1/m16/settlements/${id}`);

  if (sq.loading) return <Spinner label="Loading settlement…" />;
  if (sq.error) return <ErrorNote message={sq.error} />;
  if (!sq.data) return <ErrorNote message="Not found" />;
  const s = sq.data;
  const grievance = s.conciliation?.grievance;

  const isWorker = Boolean(persona?.nin) && persona?.nin === grievance?.workerNin;
  const isEmployer = persona?.kind === 'employer' && Boolean(persona.actingForBusinessId) && persona.actingForBusinessId === grievance?.employerEntityId;

  return (
    <div>
      <PageHeader
        title={grievance ? `Settlement — ${grievance.refNo}` : 'Settlement'}
        subtitle="E-signed by both worker and employer (both require identity tier T2+); fully-executed issues a DocVault document."
        actions={<StatusBadge status={s.status} />}
      />

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="card p-5 lg:col-span-2">
          <h2 className="mb-2 text-sm font-bold text-slate-700">Terms</h2>
          <p className="whitespace-pre-wrap text-sm text-slate-700">{s.termsText}</p>

          {s.termsDocRef ? (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-xs text-emerald-800">
              <div className="font-semibold">DocVault document issued</div>
              <div className="mt-0.5 font-mono">
                {s.termsDocRef} · sha256 {s.termsDocSha256?.slice(0, 16)}…
              </div>
            </div>
          ) : null}

          {grievance ? (
            <div className="mt-4 border-t border-slate-100 pt-3 text-xs">
              <Link className="font-semibold text-brand-600 hover:underline" to={`/grievances/${grievance.id}`}>
                View grievance →
              </Link>
              {' · '}
              <Link className="font-semibold text-brand-600 hover:underline" to={`/conciliation/${s.conciliationId}`}>
                View conciliation →
              </Link>
            </div>
          ) : null}
        </div>

        <div className="space-y-4">
          <div className="card p-4 text-sm">
            <h2 className="mb-2 text-xs font-bold uppercase text-slate-500">Signatures</h2>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-slate-600">Worker</span>
              {s.workerSignedAt ? <StatusBadge status="WORKER_SIGNED" /> : <span className="text-xs text-slate-400">Not yet signed</span>}
            </div>
            {s.workerSignedAt ? <div className="mb-2 text-[11px] text-slate-400">{formatDateTime(s.workerSignedAt)}</div> : null}
            <div className="mb-2 flex items-center justify-between">
              <span className="text-slate-600">Employer</span>
              {s.employerSignedAt ? <StatusBadge status="EMPLOYER_SIGNED" /> : <span className="text-xs text-slate-400">Not yet signed</span>}
            </div>
            {s.employerSignedAt ? <div className="text-[11px] text-slate-400">{formatDateTime(s.employerSignedAt)}</div> : null}
          </div>

          {s.status !== 'FULLY_EXECUTED' ? (
            <SignCard settlementId={s.id} asWorker={isWorker} asEmployer={isEmployer} actingFor={persona?.actingForBusinessId} onSigned={sq.reload} />
          ) : (
            <InfoNote message="Fully executed — both parties have e-signed." />
          )}
        </div>
      </div>
    </div>
  );
}

function SignCard({
  settlementId,
  asWorker,
  asEmployer,
  actingFor,
  onSigned,
}: {
  settlementId: string;
  asWorker: boolean;
  asEmployer: boolean;
  actingFor?: string;
  onSigned: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sign = async (party: 'worker' | 'employer') => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m16/settlements/${settlementId}/sign`, {}, party === 'employer' ? { actingFor } : undefined);
      onSigned();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Sign failed');
    } finally {
      setBusy(false);
    }
  };

  if (!asWorker && !asEmployer) {
    return (
      <InfoNote message="Only the worker (signed in with their own NIN) or the employer (signed in with a matching X-Acting-For mandate) may e-sign — no staff bypass exists for this action, by design." />
    );
  }

  return (
    <div className="card p-4">
      <h2 className="mb-2 text-sm font-bold text-slate-700">E-sign</h2>
      {error ? (
        <div className="mb-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {asWorker ? (
        <button className="btn-success w-full" onClick={() => sign('worker')} disabled={busy}>
          {busy ? 'Signing…' : 'Sign as worker'}
        </button>
      ) : null}
      {asEmployer ? (
        <button className="btn-success mt-2 w-full" onClick={() => sign('employer')} disabled={busy}>
          {busy ? 'Signing…' : 'Sign as employer'}
        </button>
      ) : null}
    </div>
  );
}
